import React, { useCallback } from "react";
import { connect } from "react-redux";

import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/map/types";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";

import {
  CustomizeMenuProps,
  CustomizeMenu as GenericCustomizeMenu,
} from "@app/component/RecordList";
import useMapView from "@app/component/view/utils/useMapView";
import {
  downloadMapRecords,
  fetchRecords,
  setListViewLocationSortQuery,
  updateRecordsListCustomization,
} from "@app/scene/map/store/mapMode/actions";
import {
  getListViewLocationQuery,
  getMapRecordsListSortOrder,
} from "@app/scene/map/store/mapMode/selectors";
import { RootState } from "@app/store/rootReducer";
import { AreaSearchQuery } from "@app/types/filters/AreaSearchQuery";

interface Props extends Pick<CustomizeMenuProps, "analyticsKeyPath" | "sort" | "sortQuery"> {
  downloadDisabled: boolean;
  onDownloadMapRecords?: typeof downloadMapRecords;
  onFetchRecords: typeof fetchRecords.request;
  onSetListViewLocationSortQuery: typeof setListViewLocationSortQuery;
  onUpdateRecordsListCustomization: typeof updateRecordsListCustomization.request;
  visibleEntities?: EntityTypesSupportedByMapsPage[];
}

const CustomizeMenu: React.FC<Props> = ({
  analyticsKeyPath = "Records List",
  downloadDisabled,
  onDownloadMapRecords,
  onFetchRecords,
  onSetListViewLocationSortQuery,
  onUpdateRecordsListCustomization,
  sort,
  sortQuery,
  visibleEntities,
}) => {
  const { viewState } = useMapView();

  const handleChangeSort = useCallback(
    (sort: SortModel, searchQuery?: AreaSearchQuery) => {
      onUpdateRecordsListCustomization({ sort });
      if (searchQuery) {
        onSetListViewLocationSortQuery(searchQuery);
      }
      onFetchRecords({ request: {} });
    },
    [onFetchRecords, onSetListViewLocationSortQuery, onUpdateRecordsListCustomization]
  );

  return (
    <GenericCustomizeMenu
      analyticsKeyPath={analyticsKeyPath}
      onChangeSort={handleChangeSort}
      onDownloadRecords={downloadDisabled ? undefined : onDownloadMapRecords}
      sort={sort}
      sortQuery={sortQuery}
      visibleEntities={visibleEntities ?? viewState.visibleEntities}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  sort: getMapRecordsListSortOrder(state),
  sortQuery: getListViewLocationQuery(state),
});

const mapDispatchToProps = {
  onDownloadMapRecords: downloadMapRecords,
  onFetchRecords: fetchRecords.request,
  onSetListViewLocationSortQuery: setListViewLocationSortQuery,
  onUpdateRecordsListCustomization: updateRecordsListCustomization.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomizeMenu);
